import {
  Button,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { KYDTicketType } from "@common/interfaces/KYDTicket";
import { ErrorMessage } from "@hookform/error-message";
import { useContext, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { isTicketTypeSystem } from "../../../../utils";
import { AlgoliaAttendeeObject } from "@common/interfaces/Algolia";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { AlgoliaAttendeeWithTickets } from "./AttendeesSection";

export const RefundDialogContext = createDialogContext();

export type RefundDialogValues = {
  ticket_types: Array<
    KYDTicketType & { owned_quantity: number; to_refund?: number }
  >;
  retain_ticket: boolean;
};

const RefundDialog = ({
  onSave,
  tickets = [],
  isLoading = false,
  ticket_types = [],
  user_id,
  objectID,
}: {
  isLoading: boolean;
  tickets?: AlgoliaAttendeeWithTickets["tickets"];
  user_id?: string;
  objectID?: string;
  ticket_types: KYDTicketType[];
  onSave: (data: {
    tickets: { ticket_type_id: string; id: string }[];
    user_id: string;
    retain_ticket: boolean;
    objectID: string;
  }) => void;
}) => {
  const { isOpen, close } = useContext(RefundDialogContext);

  const ticketTypesWithTickets = useMemo(() => {
    if (!tickets) return [];

    const uniqueTicketTypeIds = Array.from(
      new Set(tickets.map((t) => t.ticket_type_id))
    );

    return uniqueTicketTypeIds.map((id) => ({
      ...ticket_types.find((tt) => tt.id === id),
      owned_quantity: tickets.filter((t) => t.ticket_type_id === id).length,
      to_refund: 0,
    }));
  }, [tickets, ticket_types]);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isValid },
    control,
    watch,
  } = useForm({
    mode: "all",
    criteriaMode: "all",
    values: {
      ticket_types: ticketTypesWithTickets,
      retain_ticket: false,
    },
    disabled: isLoading,
  });

  const { fields } = useFieldArray({ name: "ticket_types", control });

  const watchFieldArray = watch("ticket_types");
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const _onSave = (data: RefundDialogValues) => {
    const toRefundFromTicketType = data.ticket_types.map((tt) => ({
      ticket_type_id: tt.id,
      to_refund: tt.to_refund || 0,
    }));

    const ticketsToRefund = toRefundFromTicketType.reduce(
      (toRefundTickets, tt) => {
        return toRefundTickets.concat(
          tickets
            .filter((t) => t.ticket_type_id === tt.ticket_type_id)
            .slice(0, tt.to_refund)
        );
      },
      []
    );

    return onSave({
      tickets: ticketsToRefund,
      retain_ticket: data.retain_ticket,
      user_id,
      objectID: objectID,
    });
  };

  const onClose = () => {
    close();
    reset();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">Refund tickets</Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign={"left"}>
            <VStack spacing={2} w="full">
              <Text>
                Please select the amount of tickets you want to refund
              </Text>
              <HStack w="full">
                <VStack w="full">
                  <Text w="full">Ticket Type</Text>
                </VStack>
                <VStack
                  w="full"
                  justifyContent={"flex-end"}
                  alignItems={"flex-end"}
                >
                  <Text>Tickets to refund</Text>
                </VStack>
              </HStack>
              {controlledFields.map((field, index) => (
                <HStack gap={4} my={1} key={field.id} w="full">
                  <VStack gap={0} alignItems={"flex-start"} w="full">
                    <Text
                      my={2}
                      w="full"
                      fontWeight={field.to_refund > 0 ? "bold" : ""}
                    >
                      {field.name} (owns {field.owned_quantity})
                    </Text>
                  </VStack>

                  <VStack gap={0} w="full">
                    <HStack w="100%" justify={"flex-end"}>
                      <IconButton
                        onClick={() => {
                          setValue(
                            `ticket_types.${index}.to_refund`,
                            field.to_refund - 1,
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                        aria-label="Subtract"
                        isDisabled={field.to_refund === 0 || isLoading}
                        icon={<MinusIcon />}
                      />
                      <Text
                        fontWeight={"bold"}
                        textAlign={"center"}
                        minW={"20px"}
                      >
                        {field.to_refund}
                      </Text>
                      <IconButton
                        onClick={() => {
                          setValue(
                            `ticket_types.${index}.to_refund`,
                            field.to_refund + 1,
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                        aria-label="Add"
                        isDisabled={field.to_refund === field.owned_quantity || isLoading}
                        icon={<AddIcon />}
                      />
                    </HStack>
                  </VStack>
                </HStack>
              ))}
              <HStack
                height="100%"
                alignItems={"flex-start"}
                width={"full"}
                mt={8}
              >
                <Text>
                  Retain tickets{" "}
                  <Text
                    fontSize="xs"
                    color="muted"
                  >{`(enabling this makes refunded tickets not go back to the sellable pool)`}</Text>
                </Text>
                <Checkbox
                  {...register("retain_ticket", {
                    disabled: isLoading,
                  })}
                />
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                bg="red.500"
                color="white"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading || !isValid}
                onClick={handleSubmit((data) => _onSave(data))}
              >
                Refund
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withRefundDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={RefundDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default RefundDialog;
